import { W_THRESH, cellDistanceKm, type IncidentView } from "./incident-model.js";
import { cellsWithinRadius } from "../../map/home-radius.js";
import { cellToLatLng, cellToParent, getResolution } from "h3-js";

export type Severity = "info" | "warning" | "critical";
export type AlertKind = "new" | "escalation" | "inPath" | "reAlert" | "allClear";
const RANK: Record<Severity, number> = { info: 1, warning: 2, critical: 3 };

export interface SpreadView {
  subject: string;
  status: string;
  bearingDeg?: number;
  speedKmh?: number;
  confidence: number;
  horizonMinutes: number;
  pathCells: string[];
  windConeCells?: string[];
  windBearingDeg?: number;
  windSpeedKmh?: number;
}

/** Latest-wins fold over app.aid.spread.estimate, keyed by subject. */
export class SpreadStore {
  #bySubject = new Map<string, { seq: number; view: SpreadView }>();
  ingest(evt: { seq: number; json: Record<string, unknown> }): void {
    if (evt.json?.["$type"] !== "app.aid.spread.estimate") return;
    const p = (evt.json.payload ?? {}) as Record<string, unknown>;
    const subject = p.subject as string;
    if (!subject) return;
    const prev = this.#bySubject.get(subject);
    if (prev && prev.seq > evt.seq) return;
    this.#bySubject.set(subject, {
      seq: evt.seq,
      view: {
        subject,
        status: (p.status as string) ?? "stationary",
        bearingDeg: p.bearingDeg as number | undefined,
        speedKmh: p.speedKmh as number | undefined,
        confidence: (p.confidence as number) ?? 0,
        horizonMinutes: (p.horizonMinutes as number) ?? 0,
        pathCells: (p.pathCells as string[]) ?? [],
        windConeCells: (p.windConeCells as string[]) ?? [],
        windBearingDeg: p.windBearingDeg as number | undefined,
        windSpeedKmh: p.windSpeedKmh as number | undefined,
      },
    });
  }
  forSubject(subject: string): SpreadView | undefined {
    return this.#bySubject.get(subject)?.view;
  }
}

/** Maps a CAP severity string to an alert level (F0c Global Constraints).
 *  Extreme|Severe → critical, Moderate → warning, else → info. */
export function capLevel(capSeverity?: string): Severity {
  switch (capSeverity) {
    case "Extreme": case "Severe": return "critical";
    case "Moderate": return "warning";
    default: return "info"; // Minor, Unknown, absent
  }
}

/** alerting §4: effective severity = max(capLevel(CAP severity), proximity level).
 *  proximity level = f(confidence, proximity, inPredictedPath). */
export function deriveSeverity(
  view: IncidentView,
  distanceKm: number,
  inPath: boolean,
  radiusKm: number,
): Severity {
  const confirmed = view.W >= W_THRESH;
  const near = distanceKm <= Math.max(1, radiusKm * 0.33);
  const prox: Severity = confirmed ? ((inPath || (near && view.confidence >= 0.75)) ? "critical" : "warning") : "info";
  return RANK[capLevel(view.capSeverity)] > RANK[prox] ? capLevel(view.capSeverity) : prox;
}

export interface AlertEvent {
  subject: string;
  hazard: string;
  severity: Severity;
  kind: AlertKind;
  distanceKm: number;
  inPath: boolean;
  view: IncidentView;
  /** When > 1, this event represents N coalesced fragments of one CAP warning
   *  (set by GroupCoalescer); alert-ui renders a "· N areas" suffix. */
  groupCount?: number;
}

interface ThreadState {
  severity: Severity;
  pathSeen: Set<string>;
  inPath: boolean;
}

/** The foreground in-app alert channel (alerting §7.1): a stateful coalescer over the
 *  home subscription. One evolving thread per subject; a new cell entering the path
 *  re-alerts even within a thread (§5 — coalescing must never hide an escalation). */
export class AlertEngine {
  #threads = new Map<string, ThreadState>();
  /** The home zone cells (within radiusKm). A spread cone overlapping ANY of these — not
   *  just the exact home cell — counts as "in path", and an incident whose cone reaches the
   *  zone is alertable even if the fire itself is beyond the radius (downwind warning). */
  #zone: Set<string>;
  #zoneRes: number;
  /** alertWindCones: include the speculative wind-prior cone in path membership, so a wind
   *  cone reaching your zone alerts you (parity with the dispatcher's BEACON_ALERT_WIND_CONES).
   *  Off by default — wind cones stay display-only. */
  constructor(private homeCell: string, private radiusKm = 20, private alertWindCones = false) {
    const [lat, lng] = cellToLatLng(homeCell);
    const cells = cellsWithinRadius(lat, lng, radiusKm);
    this.#zone = new Set(cells);
    // cellsWithinRadius returns a coarser display res than the res-8 cone cells, so match a
    // cone cell against the zone via its zone-res ancestor (hierarchical, like the dispatcher).
    this.#zoneRes = cells.length ? getResolution(cells[0]) : 8;
  }

  /** Is a (res-8) cone cell inside the home zone? Hierarchical: its zone-res ancestor is in the set. */
  #inZone(cell: string): boolean {
    if (this.#zone.has(cell)) return true;
    return getResolution(cell) >= this.#zoneRes && this.#zone.has(cellToParent(cell, this.#zoneRes));
  }

  #coneFor(subject: string, spread: SpreadStore): string[] {
    const sv = spread.forSubject(subject);
    return this.alertWindCones
      ? [...(sv?.pathCells ?? []), ...(sv?.windConeCells ?? [])]
      : (sv?.pathCells ?? []);
  }

  evaluate(
    inRadius: Array<{ view: IncidentView; distanceKm: number }>,
    all: IncidentView[],
    spread: SpreadStore,
  ): AlertEvent[] {
    const out: AlertEvent[] = [];
    const present = new Set<string>();

    // Candidates: everything in radius, PLUS any incident whose cone reaches the home zone
    // (a downwind fire beyond the radius). The latter get a home-cell distance.
    const candidates = [...inRadius];
    const inR = new Set(inRadius.map((e) => e.view.subject));
    for (const view of all) {
      if (inR.has(view.subject)) continue;
      if (this.#coneFor(view.subject, spread).some((c) => this.#inZone(c))) {
        candidates.push({ view, distanceKm: cellDistanceKm(this.homeCell, view.cell) });
      }
    }

    for (const { view, distanceKm } of candidates) {
      present.add(view.subject);
      const cone = this.#coneFor(view.subject, spread);
      const inPath = cone.some((c) => this.#inZone(c));
      const severity = deriveSeverity(view, distanceKm, inPath, this.radiusKm);
      const prev = this.#threads.get(view.subject);
      const base = { subject: view.subject, hazard: view.hazard, severity, distanceKm, inPath, view };

      if (!prev) {
        this.#threads.set(view.subject, { severity, pathSeen: new Set(cone), inPath });
        out.push({ ...base, kind: "new" });
        continue;
      }
      const newConeCell = cone.some((c) => !prev.pathSeen.has(c));
      const nowInPath = inPath && !prev.inPath;
      let kind: AlertKind | null = null;
      // A new cell bringing home into the cone is the most specific, most actionable
      // signal (§5 "new cell entering your path") — it wins over a generic escalation
      // even though entering the path is itself what raised the severity.
      if (nowInPath && newConeCell) kind = "inPath";
      else if (RANK[severity] > RANK[prev.severity]) kind = "escalation";
      // unchanged or de-escalated within radius -> coalesce (no emit)
      this.#threads.set(view.subject, {
        severity,
        pathSeen: new Set([...prev.pathSeen, ...cone]),
        inPath: inPath || prev.inPath,
      });
      if (kind) out.push({ ...base, kind });
    }

    for (const [subject, st] of [...this.#threads]) {
      if (!present.has(subject)) {
        this.#threads.delete(subject);
        out.push({
          subject, hazard: "other", severity: st.severity, kind: "allClear",
          distanceKm: 0, inPath: false,
          view: {
            subject, hazard: "other", status: "closed", W: 0, confidence: 0,
            cells: [], distinctIdentities: 0, cell: "", createdAtIso: "",
          },
        });
      }
    }
    return out;
  }
}
